<template>
  <div class="overlap-operation-mode">
    <operation-context-alert>
      {{ contextText }}
    </operation-context-alert>

    <operation-summary-strip :items="summaryItems" />

    <operation-panel
      title="Aktif kalacak ilişkiyi seç"
      subtitle="Seçim, faturalı dönemler korunarak güvenli çözüm planına dönüştürülür."
      tone="primary"
      chip-label="Seçim"
      chip-color="primary"
    >
      <div class="overlap-operation-mode__options">
        <button
          v-for="relation in relations"
          :key="relation.id"
          type="button"
          class="overlap-operation-mode__option"
          :class="{
            'overlap-operation-mode__option--active':
              relation.id === selectedRelationId,
          }"
          @click="updateSelectedRelation(relation.id)"
        >
          <div class="overlap-operation-mode__option-header">
            <div>
              <div class="overlap-operation-mode__option-title">
                {{ relation.customer_name }}
              </div>
              <div class="overlap-operation-mode__option-period">
                {{ formatDateRange(relation.starts_on, relation.ends_on) }}
              </div>
            </div>
            <div class="overlap-operation-mode__badges">
              <v-chip
                v-if="relation.is_current_owner"
                x-small
                outlined
                color="primary"
              >
                Güncel fatura sahibi
              </v-chip>
              <v-chip
                v-if="relation.id === targetRelationId"
                x-small
                outlined
                color="error"
              >
                Risk kaydı
              </v-chip>
              <v-chip
                x-small
                outlined
                :color="
                  relation.invoice_summary?.has_invoice_items
                    ? 'warning'
                    : 'success'
                "
              >
                {{
                  relation.invoice_summary?.has_invoice_items
                    ? "Fatura var"
                    : "Fatura yok"
                }}
              </v-chip>
            </div>
          </div>

          <div class="overlap-operation-mode__option-grid">
            <div>
              <div class="overlap-operation-mode__meta-label">
                Faturalı dönem
              </div>
              <div class="overlap-operation-mode__meta-value">
                {{ getInvoiceSummaryLabel(relation.invoice_summary) }}
              </div>
            </div>
            <div>
              <div class="overlap-operation-mode__meta-label">Ürün geçmişi</div>
              <div class="overlap-operation-mode__meta-value">
                {{
                  relation.products && relation.products.length
                    ? `${relation.products.length} kayıt`
                    : "Ürün yok"
                }}
              </div>
            </div>
          </div>
        </button>
      </div>
    </operation-panel>

    <operation-panel
      v-if="selectedResolutionOption"
      title="Seçimin sonucu"
      :subtitle="selectedOptionSubtitle"
      :tone="selectedResolutionOption.can_apply ? 'success' : 'warning'"
      :chip-label="
        selectedResolutionOption.can_apply ? 'Güvenli plan' : 'Kontrol'
      "
      :chip-color="selectedResolutionOption.can_apply ? 'success' : 'warning'"
    >
      <div class="overlap-operation-mode__result">
        <div class="overlap-operation-mode__result-row">
          <div class="overlap-operation-mode__result-label">
            Seçilen müşteri
          </div>
          <div class="overlap-operation-mode__result-value">
            {{ selectedResolutionOption.selected_customer_name || "-" }}
          </div>
        </div>
        <div class="overlap-operation-mode__result-row">
          <div class="overlap-operation-mode__result-label">
            Güvenli başlangıç tarihi
          </div>
          <div class="overlap-operation-mode__result-value">
            {{ formatDate(selectedResolutionOption.safe_starts_on) }}
          </div>
        </div>
      </div>

      <operation-blocked-reasons
        v-if="selectedResolutionOption.blocked_reasons?.length"
        :reasons="selectedResolutionOption.blocked_reasons"
        title="Bu seçim uygulama öncesi manuel kontrol gerektiriyor"
        class="mb-4"
      />

      <operation-timeline
        :items="selectedResolutionOption.actions || []"
        :default-status="
          selectedResolutionOption.can_apply ? 'Hazır' : 'Kontrol'
        "
        :default-color="
          selectedResolutionOption.can_apply ? 'success' : 'warning'
        "
      />
    </operation-panel>
  </div>
</template>

<script>
import OperationBlockedReasons from "../shared/OperationBlockedReasons.vue";
import OperationContextAlert from "../shared/OperationContextAlert.vue";
import OperationPanel from "../shared/OperationPanel.vue";
import OperationSummaryStrip from "../shared/OperationSummaryStrip.vue";
import OperationTimeline from "../shared/OperationTimeline.vue";
import {
  formatDate,
  formatDateRange,
  getInvoiceSummaryLabel,
} from "./modeFormatters";

export default {
  name: "OverlapRelationOperationMode",
  components: {
    OperationBlockedReasons,
    OperationContextAlert,
    OperationPanel,
    OperationSummaryStrip,
    OperationTimeline,
  },
  props: {
    draft: {
      type: Object,
      required: true,
    },
    preview: {
      type: Object,
      default: null,
    },
  },
  computed: {
    clusterName() {
      return this.preview?.cluster?.name || "Seçilen TYA";
    },
    currentCustomerName() {
      return this.preview?.cluster?.current_customer_name || "-";
    },
    relations() {
      return this.preview?.relations || [];
    },
    targetRelationId() {
      return this.preview?.target_relation_id || null;
    },
    defaultSelectedRelationId() {
      return this.preview?.default_selected_relation_id || null;
    },
    selectedRelationId() {
      return (
        this.draft?.relation_selection?.active_customer_cluster_id ||
        this.defaultSelectedRelationId
      );
    },
    resolutionOptions() {
      return this.preview?.resolution_options || [];
    },
    selectedResolutionOption() {
      return (
        this.resolutionOptions.find(
          (option) => option.selected_relation_id === this.selectedRelationId
        ) || null
      );
    },
    selectedRelation() {
      return (
        this.relations.find(
          (relation) => relation.id === this.selectedRelationId
        ) || null
      );
    },
    contextText() {
      return `${this.clusterName} için çakışan müşteri ilişkileri bulundu. Aktif kalacak ilişki seçildiğinde sistem faturalı dönemleri koruyan çözüm planını hesaplar.`;
    },
    selectedOptionSubtitle() {
      if (!this.selectedResolutionOption) {
        return "Seçim yapılınca güvenli başlangıç ve çözüm planı görünür.";
      }

      return this.selectedResolutionOption.can_apply
        ? "Bu seçim güvenli bir çözüm planı üretiyor."
        : "Bu seçimde policy engelleri var; önizleme adımında da kontrol edilebilir.";
    },
    summaryItems() {
      return [
        {
          label: "TYA",
          value: this.clusterName,
        },
        {
          label: "Güncel fatura sahibi",
          value: this.currentCustomerName,
        },
        {
          label: "Çakışan ilişki",
          value: `${this.relations.length} kayıt`,
        },
        {
          label: "Seçili ilişki",
          value: this.selectedRelation?.customer_name || "-",
        },
      ];
    },
  },
  methods: {
    formatDate,
    formatDateRange,
    getInvoiceSummaryLabel,
    updateSelectedRelation(value) {
      this.$emit("update-draft", {
        namespace: "relation_selection",
        field: "active_customer_cluster_id",
        value,
      });
    },
  },
};
</script>

<style scoped>
.overlap-operation-mode__options {
  display: grid;
  gap: 16px;
}

.overlap-operation-mode__option {
  background: #ffffff;
  border: 1px solid #d9d9e8;
  border-radius: 6px;
  cursor: pointer;
  display: block;
  padding: 18px 20px;
  text-align: left;
  transition: border-color 0.16s ease, box-shadow 0.16s ease,
    background-color 0.16s ease;
  width: 100%;
}

.overlap-operation-mode__option:hover {
  border-color: #5867dd;
}

.overlap-operation-mode__option--active {
  background: #f4f6ff;
  border-color: #5867dd;
  box-shadow: inset 0 0 0 1px rgba(88, 103, 221, 0.18);
}

.overlap-operation-mode__option-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.overlap-operation-mode__option-title {
  color: #212121;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
}

.overlap-operation-mode__option-period {
  color: #757575;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
}

.overlap-operation-mode__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.overlap-operation-mode__option-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 16px;
}

.overlap-operation-mode__meta-label,
.overlap-operation-mode__result-label {
  color: #8d8d8d;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

.overlap-operation-mode__meta-value,
.overlap-operation-mode__result-value {
  color: #2b2b2b;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
}

.overlap-operation-mode__result {
  margin-bottom: 18px;
}

.overlap-operation-mode__result-row + .overlap-operation-mode__result-row {
  margin-top: 14px;
}

@media (max-width: 960px) {
  .overlap-operation-mode__option-header {
    flex-direction: column;
  }

  .overlap-operation-mode__badges {
    justify-content: flex-start;
  }

  .overlap-operation-mode__option-grid {
    grid-template-columns: 1fr;
  }
}
</style>
